import React, { useState, useCallback } from 'react';
import { Card, Upload, Button, message, Progress, Alert, Space, Typography, Divider } from 'antd';
import { InboxOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import api from '../utils/axios';

const { Title, Text } = Typography;
const { Dragger } = Upload;

interface ImportResult {
  success: boolean;
  message: string;
  imported: number;
  skipped: number;
  errors: string[];
}

const InsurerImport: React.FC = () => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [dryRun, setDryRun] = useState(false);

  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = `${api.defaults.baseURL}/insurers/import/template`;
    link.download = 'insurers-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleUpload = useCallback(async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    setImportResult(null);

    try {
      const response = await api.post(`/insurers/import?dryRun=${dryRun}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Upload progress: ${percent}%`);
          }
        },
      });

      const result = response.data;
      setImportResult(result);

      if (result.success) {
        message.success(`Import completed! ${result.imported} insurers imported, ${result.skipped} skipped`);
      } else {
        message.error(`Import failed: ${result.message}`);
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Upload failed';
      message.error(errorMessage);
      setImportResult({
        success: false,
        message: errorMessage,
        imported: 0,
        skipped: 0,
        errors: [errorMessage]
      });
    } finally {
      setUploading(false);
      setFileList([]);
    }
  }, [dryRun]);

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    fileList,
    beforeUpload: (file) => {
      const isCSV = file.type === 'text/csv' || file.name.endsWith('.csv');
      if (!isCSV) {
        message.error('You can only upload CSV files!');
        return false;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('File must be smaller than 5MB!');
        return false;
      }
      return false; // Prevent auto upload
    },
    onChange: (info) => {
      setFileList(info.fileList);
    },
    onDrop(e) {
      console.log('Dropped files', e.dataTransfer.files);
    },
  };

  const handleImport = () => {
    if (fileList.length === 0) {
      message.error('Please select a file first');
      return;
    }
    const file = fileList[0].originFileObj;
    if (file) {
      handleUpload(file);
    }
  };

  return (
    <Card title="Import Insurers" style={{ maxWidth: 800, margin: '0 auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Instructions */}
        <div>
          <Title level={4}>Instructions</Title>
          <Text>
            Upload a CSV file containing insurer data. The file should have the following columns:
          </Text>
          <ul>
            <li><Text code>name</Text> - Required: The insurer's name</li>
            <li><Text code>code</Text> - Optional: A short code for the insurer</li>
          </ul>
        </div>

        {/* Template Download */}
        <div>
          <Button 
            icon={<DownloadOutlined />} 
            onClick={handleDownloadTemplate}
            type="dashed"
          >
            Download Template
          </Button>
        </div>

        <Divider />

        {/* Dry Run Toggle */}
        <div>
          <Text>Test Mode: </Text>
          <Button 
            type={dryRun ? 'primary' : 'default'}
            size="small"
            onClick={() => setDryRun(!dryRun)}
          >
            {dryRun ? 'ON' : 'OFF'}
          </Button>
          <Text type="secondary" style={{ marginLeft: 8 }}>
            {dryRun ? 'Will show what would be imported without actually importing' : 'Will import data immediately'}
          </Text>
        </div>

        {/* File Upload */}
        <Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag CSV file to this area to upload</p>
          <p className="ant-upload-hint">
            Support for a single CSV file. File size limit: 5MB.
          </p>
        </Dragger>

        {/* Import Button */}
        <div style={{ textAlign: 'center' }}>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={handleImport}
            loading={uploading}
            disabled={fileList.length === 0}
            size="large"
          >
            {dryRun ? 'Test Import' : 'Import Insurers'}
          </Button>
        </div>

        {/* Progress */}
        {uploading && (
          <div>
            <Text>Processing...</Text>
            <Progress percent={100} status="active" />
          </div>
        )}

        {/* Results */}
        {importResult && (
          <div>
            <Alert
              message={importResult.success ? 'Import Successful' : 'Import Failed'}
              description={
                <div>
                  <p><strong>Message:</strong> {importResult.message}</p>
                  {importResult.success && (
                    <>
                      <p><strong>Imported:</strong> {importResult.imported}</p>
                      <p><strong>Skipped:</strong> {importResult.skipped}</p>
                    </>
                  )}
                  {importResult.errors.length > 0 && (
                    <div>
                      <p><strong>Errors:</strong></p>
                      <ul>
                        {importResult.errors.map((error, index) => (
                          <li key={index} style={{ color: 'red' }}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              }
              type={importResult.success ? 'success' : 'error'}
              showIcon
            />
          </div>
        )}
      </Space>
    </Card>
  );
};

export default InsurerImport; 